// L'EFFACEUR — Hook auto-save de tunnel
// Sauvegarde l'état d'un tunnel toutes les N secondes dans localStorage.
// Optionnellement, envoie aussi au backend si une URL API est fournie.
//
// Usage :
//   const { restored, clear } = useAutoSave('demenagement_form', formData, setFormData);
//
// Quand le user revient, formData est restauré automatiquement.

import { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const STORAGE_PREFIX = 'leffaceur_autosave_';
const SAVE_INTERVAL_MS = 30000; // 30 secondes
const MAX_AGE_DAYS = 7; // expire après 7 jours

export const useAutoSave = (key, data, setData, options = {}) => {
  const { remoteEndpoint = null, onRestore = null } = options;
  const [restored, setRestored] = useState(false);
  const lastSavedRef = useRef(null);

  // Restoration au mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_PREFIX + key);
      if (!raw) return;
      const saved = JSON.parse(raw);
      // Vérifie l'âge
      const ageDays = (Date.now() - saved.timestamp) / (1000 * 60 * 60 * 24);
      if (ageDays > MAX_AGE_DAYS) {
        localStorage.removeItem(STORAGE_PREFIX + key);
        return;
      }
      // Restaure
      if (saved.data && setData) {
        setData(saved.data);
        setRestored(true);
        if (onRestore) onRestore(saved.data, saved.timestamp);
      }
    } catch (e) {
      console.warn('[AutoSave] Restore failed:', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Sauvegarde périodique
  useEffect(() => {
    if (!data) return;
    const dataStr = JSON.stringify(data);
    if (dataStr === lastSavedRef.current) return; // pas de changement

    const timer = setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify({
          data,
          timestamp: Date.now(),
        }));
        lastSavedRef.current = dataStr;
        // Optionnel : push au backend
        if (remoteEndpoint) {
          const token = localStorage.getItem('token');
          axios.post(remoteEndpoint, { key, data }, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }).catch(() => {/* silent */});
        }
      } catch (e) {
        // localStorage plein ou navigation privée → silent
      }
    }, SAVE_INTERVAL_MS);

    return () => clearTimeout(timer);
  }, [data, key, remoteEndpoint]);

  const clear = () => {
    localStorage.removeItem(STORAGE_PREFIX + key);
    setRestored(false);
    lastSavedRef.current = null;
  };

  return { restored, clear };
};

export default useAutoSave;
